import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n{{#if this.isOpen}}\n  <GlobalSearch::KeyboardNavigation\n    @runIntent={{this.runIntent}}\n    @scrollIntoActiveResultItem={{this.scrollIntoActiveResultItem}}\n    @searchResults={{this.allSearchResults}}\n    @selectedResultIndex={{this.selectedResultIndex}}\n    @setSelectedResultIndex={{this.setSelectedResultIndex}}\n  />\n  <Shared::Modal\n    @close={{this.close}}\n    @focusTrap={{true}}\n    class=\"is--top h--globalSearch__modalWrapper\"\n    data-test-id=\"global-search\"\n    {{did-insert this.onOpen}}\n    as |modal|\n  >\n    <modal.card @closeIcon={{false}} class=\"h--globalSearch__modal\">\n      <GlobalSearch::SearchInput\n        @isRunning={{this.isSearching}}\n        @onBlur={{this.clearSelection}}\n        @onChange={{this.search}}\n      />\n      {{#if this.showNoResults}}\n        <GlobalSearch::NoResults />\n      {{/if}}\n      {{!-- <GlobalSearch::DebugQuery @query={{this.parsedQuery}} /> --}}\n\n      <GlobalSearch::SearchResultList @results={{this.allSearchResults}} as |result index|>\n        <GlobalSearch::SearchResult\n          @isSelected={{is-equal index this.selectedResultIndex}}\n          @result={{result}}\n          @runIntent={{fn this.runIntent result}}\n        />\n      </GlobalSearch::SearchResultList>\n\n      <GlobalSearch::SearchFooter @footer={{this.footer}} />\n    </modal.card>\n  </Shared::Modal>\n{{/if}}", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n{{#if this.isOpen}}\n  <GlobalSearch::KeyboardNavigation\n    @runIntent={{this.runIntent}}\n    @scrollIntoActiveResultItem={{this.scrollIntoActiveResultItem}}\n    @searchResults={{this.allSearchResults}}\n    @selectedResultIndex={{this.selectedResultIndex}}\n    @setSelectedResultIndex={{this.setSelectedResultIndex}}\n  />\n  <Shared::Modal\n    @close={{this.close}}\n    @focusTrap={{true}}\n    class=\"is--top h--globalSearch__modalWrapper\"\n    data-test-id=\"global-search\"\n    {{did-insert this.onOpen}}\n    as |modal|\n  >\n    <modal.card @closeIcon={{false}} class=\"h--globalSearch__modal\">\n      <GlobalSearch::SearchInput\n        @isRunning={{this.isSearching}}\n        @onBlur={{this.clearSelection}}\n        @onChange={{this.search}}\n      />\n      {{#if this.showNoResults}}\n        <GlobalSearch::NoResults />\n      {{/if}}\n      {{!-- <GlobalSearch::DebugQuery @query={{this.parsedQuery}} /> --}}\n\n      <GlobalSearch::SearchResultList @results={{this.allSearchResults}} as |result index|>\n        <GlobalSearch::SearchResult\n          @isSelected={{is-equal index this.selectedResultIndex}}\n          @result={{result}}\n          @runIntent={{fn this.runIntent result}}\n        />\n      </GlobalSearch::SearchResultList>\n\n      <GlobalSearch::SearchFooter @footer={{this.footer}} />\n    </modal.card>\n  </Shared::Modal>\n{{/if}}","moduleName":"later/components/global-search.hbs","parseOptions":{"srcName":"later/components/global-search.hbs"}});
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { uniqBy } from 'lodash';

import { SearchResultType } from 'later/services/global-search';

import type IntlService from 'ember-intl/services/intl';
import type CacheService from 'later/services/cache';
import type GlobalSearchService from 'later/services/global-search';
import type { SearchResult } from 'later/services/global-search';
import type { SearchQuery } from 'later/utils/global-search/search-query';
import type { EmptyObject } from 'shared/types';

const MAX_PREVIOUSLY_USED_RESULTS = 2;
const DEBOUNCE_TIMEOUT_MS = 300;

export default class GlobalSearchComponent extends Component {
  @service declare globalSearch: GlobalSearchService;
  @service declare intl: IntlService;
  @service declare cache: CacheService;

  @tracked searchResults: SearchResult[] = [];
  @tracked query = '';
  @tracked selectedResultIndex = -1;
  @tracked previouslyUsedSearchResults: SearchResult[] = [];
  @tracked suggestedSearchResults: SearchResult[] = [];

  constructor(owner: unknown, args: EmptyObject) {
    super(owner, args);

    this.suggestedSearchResults = this.globalSearch.getSuggestedSearchResults();
    this.selectedResultIndex = 0;

    const cachedPreviouslyUsedSearchResults = this.cache.retrieve('previouslyUsedSearchResults') as string;
    if (cachedPreviouslyUsedSearchResults) {
      this.previouslyUsedSearchResults = JSON.parse(cachedPreviouslyUsedSearchResults);
    }
  }

  get isOpen(): boolean {
    return this.globalSearch.showGlobalSearch;
  }

  get parsedQuery(): SearchQuery {
    return this.globalSearch.parseQuery(this.query);
  }

  @action
  onOpen(): void {
    if (this.allSearchResults.length > 0) {
      this.selectedResultIndex = 0;
    }
  }

  @action
  close(): void {
    this.query = '';
    this.searchResults = [];
    this.clearSelection();
    this.globalSearch.closeSearchModal();
  }

  @action
  scrollIntoActiveResultItem(blockPosition: string): void {
    const results = document.querySelectorAll('li.h--globalSearch__result-item button');
    if (results.length === 0) {
      return;
    }

    const currentItem = results[this.selectedResultIndex];
    currentItem?.scrollIntoView({
      behavior: 'smooth',
      block: blockPosition as ScrollLogicalPosition,
      inline: 'nearest'
    });
  }

  @action
  setSelectedResultIndex(index: number): void {
    this.selectedResultIndex = index;
  }

  get footer(): { text: string; icon?: string } | undefined {
    const type = this.allSearchResults?.[this.selectedResultIndex]?.type || this.allSearchResults[0]?.type;

    switch (type) {
      case SearchResultType.Live:
        return;
      case SearchResultType.PreviouslyUsed:
        return { text: this.intl.t('search_result_type.recent'), icon: 'clock' };
      case SearchResultType.Suggested:
        return { text: this.intl.t('search_result_type.suggested_action'), icon: 'hint' };
      default:
        return;
    }
  }

  get showNoResults(): boolean {
    return this.searchResults.length === 0 && this.query.length > 2 && !this.search.isRunning;
  }

  get allSearchResults(): SearchResult[] {
    if (this.searchResults.length !== 0) {
      return this.searchResults;
    }

    const filteredResults = uniqBy(
      [...this.previouslyUsedSearchResults, ...this.suggestedSearchResults],
      'description'
    );
    return filteredResults;
  }

  get isSearching(): boolean {
    return this.globalSearch.search.isRunning;
  }

  search = task({ restartable: true }, async (query) => {
    this.query = query;

    await timeout(DEBOUNCE_TIMEOUT_MS);

    this.searchResults = await this.globalSearch.search.perform(this.parsedQuery);

    if (this.searchResults.length === 0) {
      this.clearSelection();
      return;
    }

    // Reset the selected result index to 0 when the search results change
    this.selectedResultIndex = 0;
  });

  @action
  clearSelection(): void {
    this.selectedResultIndex = -1;
  }

  @action
  runIntent(result: SearchResult): void {
    let query = this.parsedQuery;

    if (result.type === SearchResultType.PreviouslyUsed && result.query) {
      // eslint-disable-next-line prefer-destructuring
      query = result.query;
    }

    this.globalSearch.runIntent(result.intent, query);
    this.addSearchResultToPreviouslyUsed(result, query);
    this.close();
  }

  addSearchResultToPreviouslyUsed(result: SearchResult, query: SearchQuery): void {
    const newResult = { ...result, type: SearchResultType.PreviouslyUsed, query };

    this.previouslyUsedSearchResults = uniqBy([newResult, ...this.previouslyUsedSearchResults], 'description').slice(
      0,
      MAX_PREVIOUSLY_USED_RESULTS
    );
    this.cache.add('previouslyUsedSearchResults', JSON.stringify(this.previouslyUsedSearchResults), {
      expiry: this.cache.maxExpiryDate(),
      persist: true
    });
  }
}
